// frontend/src/components/layout/Navbar.jsx
import RouteSelector from '../common/RouteSelector';
import IconSun from '../icons/IconSun';
import IconMoon from '../icons/IconMoon';
import StatusDot from '../common/StatusDot';
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { classifyLiveStatus } from '@core/domain/status';
import { branding } from '@core/config/branding';

const LIVE_LABELS = {
  connecting: 'CONNECTING',
  live: 'LIVE',
  offline: 'OFFLINE',
};

const LIVE_COLORS = {
  connecting: 'var(--status-amber)',
  live: 'var(--live-green)',
  offline: 'var(--live-red)',
};

const BASE_TABS = [
  { key: 'visualiser', label: 'Route Visualiser' },
  { key: 'arrivals',   label: 'Arrivals Board' },
  { key: 'metrics',    label: 'Live Metrics' },
];

export default function Navbar({
  dark,
  onToggleDark,
  routes,
  selectedRoute,
  onRouteChange,
  view,
  onViewChange,
  isAuthenticated,
  onLoginClick,
  isAdmin,
  userEmail,
  dataLoading,
  liveLoading,
  liveError,
  liveLastUpdated,
}) {
  const { logout } = useAuth();

  const liveStatus = classifyLiveStatus({
    loading: liveLoading,
    error: liveError,
    hasData: Boolean(liveLastUpdated),
  });
  const liveColor = LIVE_COLORS[liveStatus];
  const liveLabel = LIVE_LABELS[liveStatus];

  const tabs = isAdmin
    ? [...BASE_TABS, { key: 'admin', label: 'Admin' }]
    : BASE_TABS;
  const activePageLabel = tabs.find(tab => tab.key === view)?.label || 'Current Page';

  const logoSrc = dark ? branding.logoDark : branding.logoLight;

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        height: 'var(--navbar-height)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        gap: 20,
        background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <img
        src={logoSrc}
        alt={branding.name}
        style={{
          height: 32,
          width: 'auto',
          cursor: 'default',
        }}
      />

      <RouteSelector routes={routes} selected={selectedRoute} onChange={onRouteChange} />

      <div style={{ width: 1, height: 28, background: 'var(--border)', flexShrink: 0 }} />

      <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => onViewChange(tab.key)}
            style={{
              padding: '6px 16px',
              height: 34,
              border: '1px solid',
              borderColor: view === tab.key ? 'var(--accent)' : 'transparent',
              borderRadius: 8,
              background: view === tab.key ? 'var(--accent-dim)' : 'transparent',
              color: view === tab.key ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              if (view !== tab.key) e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={e => {
              if (view !== tab.key) e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      {dataLoading && (
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--status-amber)', whiteSpace: 'nowrap' }}>
          Data loading for page {activePageLabel}
        </span>
      )}

      {/* Live Indicator */}
      <div
        title={liveLastUpdated ? `Live data updated ${liveLastUpdated.toLocaleTimeString()}` : 'Live data status'}
        style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 16 }}
      >
        <StatusDot color={liveColor} pulse={liveStatus === 'live'} />
        <span style={{ fontSize: 11, fontWeight: 700, color: liveColor, letterSpacing: '0.05em' }}>
          {liveLabel}
        </span>
      </div>

      {/* Authentication area */}
      {isAuthenticated ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-secondary)',
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={userEmail}
          >
            {userEmail}
          </span>
          <button
            onClick={logout}
            title="Logout"
            style={{
              width: 34,
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              transition: 'all 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.style.borderColor = 'var(--text-muted)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => onLoginClick('login')}
            title="Log in"
            style={{
              padding: '6px 12px',
              height: 34,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: 12,
              fontWeight: 600,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = 'var(--text-primary)';
              e.currentTarget.style.borderColor = 'var(--text-muted)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            Log in
          </button>
          <button
            onClick={() => onLoginClick('register')}
            title="Sign up"
            style={{
              padding: '6px 12px',
              height: 34,
              background: 'var(--accent)',
              border: '1px solid var(--accent)',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'var(--text-on-accent)',
              fontSize: 12,
              fontWeight: 600,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--accent-hover)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--accent)';
            }}
          >
            Sign up
          </button>
        </div>
      )}

      {/* Theme Toggle */}
      <button
        onClick={onToggleDark}
        title={dark ? 'Light mode' : 'Dark mode'}
        style={{
          width: 34,
          height: 34,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text-secondary)',
          transition: 'all 0.15s',
          flexShrink: 0,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.color = 'var(--text-primary)';
          e.currentTarget.style.borderColor = 'var(--text-muted)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.color = 'var(--text-secondary)';
          e.currentTarget.style.borderColor = 'var(--border)';
        }}
      >
        {dark ? <IconSun /> : <IconMoon />}
      </button>
    </header>
  );
}